/**
 * Monogram derivation for financial institutions.
 *
 * This is a deliberate mirror of
 * backend/internal/core/services/institution_branding.go. Both exist because
 * the mark must be drawable before — and without — a network round trip:
 * a manually entered account has no Plaid logo to wait for, and a mark that
 * changed colour depending on whether the API answered would be worse than no
 * colour at all.
 *
 * If you change a rule here, change it there, and vice versa. The Go tests and
 * institution-monogram.test.ts assert the same values on purpose.
 */

/** Number of categorical colours available. Must equal seriesColors' length. */
export const MONOGRAM_PALETTE_SIZE = 8;

/** Joining words that carry no identity. Generic banking words stay: dropping
 *  "Bank" turns "National Bank of Canada" into "NC" instead of "NB". */
const STOP_WORDS = new Set([
  'of', 'the', 'and', 'for', 'de', 'la', 'le', 'du', 'des',
]);

/**
 * Fold a free-text name to its lookup key.
 *
 * Mirrors SQL institution_match_key() and Go NormalizeInstitutionName().
 *
 * @param name Raw institution or account name.
 * @returns Lower-cased, punctuation folded to single spaces, trimmed.
 */
export function normalizeInstitutionName(name: string): string {
  return (name ?? '')
    .toLowerCase()
    // Unicode-unaware on purpose: the Go side folds on [^a-z0-9] too, and the
    // two must agree byte for byte or the colours diverge.
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** True when every letter in the word is upper case (digits tolerated). */
function isAcronym(word: string): boolean {
  let sawLetter = false;
  for (const ch of word) {
    if (/\p{L}/u.test(ch)) {
      sawLetter = true;
      if (ch !== ch.toUpperCase()) return false;
    } else if (!/\p{Nd}/u.test(ch)) {
      return false;
    }
  }
  return sawLetter;
}

/**
 * Derive the monogram letters for a name.
 *
 * 1. Split on punctuation, discarding joining words.
 * 2. A leading two- or three-character acronym is used whole: "RBC Royal Bank"
 *    reads "RBC", not "RR".
 * 3. A single remaining word yields its first two characters, which separates
 *    better down a list than one letter would.
 * 4. Otherwise the first letter of the first two words.
 *
 * @param name Institution name, or an account name to fall back on.
 * @returns One to three upper-case characters; "?" when nothing is usable.
 */
export function institutionInitials(name: string): string {
  const words = (name ?? '')
    .split(/[^\p{L}\p{Nd}]+/u)
    .filter((w) => w.length > 0 && !STOP_WORDS.has(w.toLowerCase()));

  if (words.length === 0) return '?';

  const first = Array.from(words[0]);
  if (first.length >= 2 && first.length <= 3 && isAcronym(words[0])) {
    return words[0];
  }
  if (words.length === 1) {
    return first.slice(0, 2).join('').toUpperCase();
  }
  return (first[0] + Array.from(words[1])[0]).toUpperCase();
}

/**
 * Pick a stable palette slot for a name.
 *
 * FNV-1a over the normalised name, so the same institution keeps the same
 * colour everywhere without anything being stored. Math.imul is required: a
 * plain `*` overflows into a float and stops matching Go's uint32 arithmetic.
 *
 * @param name Institution or account name.
 * @returns Index in [0, MONOGRAM_PALETTE_SIZE).
 */
export function monogramColorIndex(name: string): number {
  const key = normalizeInstitutionName(name);
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  // >>> 0 reinterprets the sign bit, matching Go's uint32.
  return (hash >>> 0) % MONOGRAM_PALETTE_SIZE;
}
